/**
 * URL state synchronization — serialize/deserialize app state to URL query params.
 *
 * Persists: map center, zoom, active desk, time range, active layers.
 * Debounces URL updates on map movement to avoid excessive history entries.
 *
 * Example URL:
 *   ?lat=48.8566&lng=2.3522&z=6.00&desk=general&time=24h&layers=earthquakes,conflicts
 */

import { appStore, type TimeRange } from '@/core/store';
import { getMap } from '@/map/engine';
import { getDeskIds } from '@/config/desk-presets';

const TIME_RANGE_VALUES: TimeRange[] = ['1h', '6h', '24h', '48h', '7d', 'all'];

/**
 * Read URL query parameters and apply them to app state.
 * Call once at boot, before panel mounting.
 */
export function restoreStateFromURL(): void {
  const params = new URLSearchParams(window.location.search);
  if (params.size === 0) return;

  const updates: Partial<ReturnType<typeof appStore.getState>> = {};

  // Desk preset (accepts both 'desk' and legacy 'variant' params)
  const validDesks = getDeskIds();
  const desk = params.get('desk') ?? params.get('variant');
  if (desk && validDesks.includes(desk)) {
    updates.variant = desk;
  }

  // Time range
  const time = params.get('time') as TimeRange | null;
  if (time && TIME_RANGE_VALUES.includes(time)) {
    updates.timeRange = time;
  }

  // Active layers
  const layersParam = params.get('layers');
  if (layersParam) {
    const layers = layersParam.split(',').map(s => s.trim()).filter(Boolean);
    if (layers.length > 0) {
      updates.activeLayers = layers;
    }
  }

  if (Object.keys(updates).length > 0) {
    appStore.setState(updates);
  }

  // Map center + zoom (applied after map loads via flyTo)
  const lat = parseFloat(params.get('lat') ?? '');
  const lng = parseFloat(params.get('lng') ?? '');
  const zoom = parseFloat(params.get('z') ?? '');

  if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
    // Wait for map to be ready, then fly to position
    const tryFly = (): void => {
      const map = getMap();
      if (map && map.loaded()) {
        map.jumpTo({
          center: [lng, lat],
          zoom: isNaN(zoom) ? undefined : Math.max(0, Math.min(zoom, 22)),
        });
      } else {
        // Retry once after a short delay
        setTimeout(() => {
          const m = getMap();
          if (m) {
            m.jumpTo({
              center: [lng, lat],
              zoom: isNaN(zoom) ? undefined : Math.max(0, Math.min(zoom, 22)),
            });
          }
        }, 1000);
      }
    };
    tryFly();
  }
}

/**
 * Start syncing app state to URL query parameters.
 * Debounces writes to avoid excessive history pushes.
 * Returns a cleanup function.
 */
export function startURLSync(): () => void {
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  const cleanupFns: (() => void)[] = [];

  function updateURL(): void {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
    }

    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      writeStateToURL();
    }, 500);
  }

  // Subscribe to state changes that should be reflected in URL
  cleanupFns.push(
    appStore.subscribe((s) => s.variant, updateURL),
  );
  cleanupFns.push(
    appStore.subscribe((s) => s.timeRange, updateURL),
  );
  cleanupFns.push(
    appStore.subscribe((s) => s.activeLayers, updateURL),
  );

  // Map move listener — may need to wait for map initialization
  const moveHandler = (): void => updateURL();
  let mapListenerAttached = false;

  function attachMapListener(): void {
    if (mapListenerAttached) return;
    const m = getMap();
    if (!m) return;
    mapListenerAttached = true;
    m.on('moveend', moveHandler);
    cleanupFns.push(() => {
      const mp = getMap();
      if (mp) mp.off('moveend', moveHandler);
    });
  }

  const map = getMap();
  if (map) {
    attachMapListener();
  } else {
    // Map not ready at boot — retry after map likely initializes
    const retryTimer = setTimeout(() => attachMapListener(), 2000);
    cleanupFns.push(() => clearTimeout(retryTimer));
  }

  return () => {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
    }
    for (const fn of cleanupFns) fn();
  };
}

/**
 * Write current app state to URL query parameters.
 * Uses replaceState to avoid polluting browser history.
 */
function writeStateToURL(): void {
  const state = appStore.getState();
  const params = new URLSearchParams();

  // Map position
  const map = getMap();
  if (map) {
    const center = map.getCenter();
    params.set('lat', center.lat.toFixed(4));
    params.set('lng', center.lng.toFixed(4));
    params.set('z', map.getZoom().toFixed(2));
  }

  // Desk (only if not default)
  if (state.variant !== 'general') {
    params.set('desk', state.variant);
  }

  // Time range (only if not default)
  if (state.timeRange !== '24h') {
    params.set('time', state.timeRange);
  }

  // Active layers (only if set)
  if (state.activeLayers.length > 0) {
    params.set('layers', state.activeLayers.join(','));
  }

  const queryString = params.toString();
  const newUrl = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname;

  try {
    window.history.replaceState(null, '', newUrl);
  } catch {
    // replaceState can fail in some contexts (e.g., cross-origin iframes)
  }
}
